import moment from 'moment'

// Option lists and colours for the Safety Incident Management Log pages.
// Mirrors server/src/models/AdminCRM/CRMP/ClinicalSafetyIncident.js - keep the
// two in step.

export const STATUS_OPTIONS = [
  'Reported',
  'Acknowledged',
  'Under Investigation',
  'Actions In Progress',
  'Awaiting Verification',
  'Closed',
]

export const OPEN_STATUSES = STATUS_OPTIONS.filter((s) => s !== 'Closed')

export const CLASSIFICATION_OPTIONS = [
  'Unclassified',
  'Safety Incident',
  'Near Miss',
  'Safety Concern',
  'Not a Safety Incident',
]

export const SOURCE_OPTIONS = [
  'In-app ticket',
  'Mobile app ticket',
  'Phone',
  'Email',
  'Live chat',
  'Customer governance',
  'Internal',
  'Monitoring',
  'Other',
]

export const PLATFORM_OPTIONS = ['Web', 'Mobile', 'Both', 'Unknown']

export const HARM_OPTIONS = [
  'Unknown',
  'Near miss',
  'No harm',
  'Low harm',
  'Moderate harm',
  'Severe harm',
  'Death',
]

export const PRIORITY_OPTIONS = [
  'P1 Immediate',
  'P2 Urgent',
  'P3 Routine',
  'P4 Low',
]

export const CONTRIBUTING_FACTOR_OPTIONS = [
  'Software defect',
  'Design or usability',
  'Configuration',
  'Training or user action',
  'Data quality or migration',
  'Third-party service',
  'Infrastructure or availability',
  'Process or documentation',
  'Unknown',
]

export const ACTION_TYPE_OPTIONS = ['Immediate', 'Corrective', 'Preventive']
export const ACTION_STATUS_OPTIONS = ['Open', 'In Progress', 'Done', 'Cancelled']

export const REPORTING_STATE_OPTIONS = [
  'Not assessed',
  'Not required',
  'Required',
  'Submitted',
]

export const REPORTING_BODIES = [
  {
    key: 'customerGovernance',
    label: 'Customer clinical governance',
    hint: 'The affected organisation(s) told, for their own DCB0160 records',
  },
  {
    key: 'lfpse',
    label: 'NHS LFPSE',
    hint: 'Learn from Patient Safety Events, usually via the customer',
  },
  { key: 'ico', label: 'ICO', hint: 'Personal data breach: 72 hours from awareness' },
  { key: 'cqc', label: 'CQC', hint: 'Via the customer where notifiable' },
]

export const SAFETY_CASE_IMPACT_OPTIONS = [
  'Not assessed',
  'No impact',
  'Hazard log updated',
  'CSCR update required',
  'CSCR updated',
]

export const YES_NO_UNKNOWN = ['Unknown', 'No', 'Yes']

export const MODULE_OPTIONS = [
  'Clinical Records',
  'Event Planning',
  'Fleet & Equipment',
  'Governance',
  'HR & Employees',
  'Command & Control',
  'Patient Transport',
  'Billing',
  'Mobile App',
  'Other',
]

export const LIKELIHOOD_OPTIONS = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain']
export const SEVERITY_OPTIONS = ['Negligible', 'Minor', 'Moderate', 'Major', 'Catastrophic']

const RISK_MATRIX = {
  'Almost Certain': ['Medium', 'High', 'High', 'Critical', 'Critical'],
  Likely: ['Low', 'Medium', 'High', 'Critical', 'Critical'],
  Possible: ['Low', 'Medium', 'Medium', 'High', 'High'],
  Unlikely: ['Low', 'Low', 'Medium', 'Medium', 'High'],
  Rare: ['Low', 'Low', 'Low', 'Low', 'Medium'],
}

export const rateRisk = (likelihood, severity) => {
  const row = RISK_MATRIX[likelihood]
  const col = SEVERITY_OPTIONS.indexOf(severity)
  if (!row || col < 0) return ''
  return row[col]
}

export const STATUS_COLOR = {
  Reported: '#B42318',
  Acknowledged: '#B54708',
  'Under Investigation': '#5B48B0',
  'Actions In Progress': '#268EBB',
  'Awaiting Verification': '#0F6670',
  Closed: '#27803B',
}

export const CLASSIFICATION_COLOR = {
  Unclassified: '#64748B',
  'Safety Incident': '#B42318',
  'Near Miss': '#B54708',
  'Safety Concern': '#5B48B0',
  'Not a Safety Incident': '#27803B',
}

export const HARM_COLOR = {
  Unknown: '#64748B',
  'Near miss': '#B54708',
  'No harm': '#27803B',
  'Low harm': '#B54708',
  'Moderate harm': '#B42318',
  'Severe harm': '#7A271A',
  Death: '#111827',
}

export const PRIORITY_COLOR = {
  'P1 Immediate': '#7A271A',
  'P2 Urgent': '#B42318',
  'P3 Routine': '#B54708',
  'P4 Low': '#64748B',
}

export const RISK_COLOR = {
  Low: '#27803B',
  Medium: '#B54708',
  High: '#B42318',
  Critical: '#7A271A',
}

export const ACTION_STATUS_COLOR = {
  Open: '#B42318',
  'In Progress': '#B54708',
  Done: '#27803B',
  Cancelled: '#64748B',
}

export const REPORTING_STATE_COLOR = {
  'Not assessed': '#64748B',
  'Not required': '#27803B',
  Required: '#B42318',
  Submitted: '#268EBB',
}

export const riskTone = (rating) => RISK_COLOR[rating] || '#64748B'

export const isSeriousHarm = (harm) =>
  ['Moderate harm', 'Severe harm', 'Death'].includes(harm)

// Service targets from the CRMP §8. Working days exclude weekends only;
// bank holidays are not modelled.
export const SLA = { acknowledgeDays: 1, classifyDays: 2 }

export const addWorkingDays = (from, days) => {
  const date = moment(from)
  let remaining = days
  while (remaining > 0) {
    date.add(1, 'day')
    if (date.isoWeekday() < 6) remaining -= 1
  }
  return date
}

export const workingDaysBetween = (from, to) => {
  let count = 0
  const cursor = moment(from).startOf('day')
  const end = moment(to).startOf('day')
  while (cursor.isBefore(end)) {
    cursor.add(1, 'day')
    if (cursor.isoWeekday() < 6) count += 1
  }
  return count
}

// Where an incident stands against the two CRMP targets.
export const slaState = (incident) => {
  if (!incident) return { acknowledge: null, classify: null }
  const reported = incident.reportedAt
  const acknowledgedAt = incident.triage?.acknowledgedAt
  const classified =
    incident.classification && incident.classification !== 'Unclassified'
  const ackDue = addWorkingDays(reported, SLA.acknowledgeDays)
  const classifyDue = addWorkingDays(reported, SLA.classifyDays)

  const acknowledge = acknowledgedAt
    ? {
        key: 'met',
        label: `Acknowledged ${moment(acknowledgedAt).fromNow()}`,
        late: moment(acknowledgedAt).isAfter(ackDue),
      }
    : moment().isAfter(ackDue)
      ? { key: 'overdue', label: `Acknowledgement overdue by ${moment().diff(ackDue, 'hours')}h` }
      : { key: 'due', label: `Acknowledge by ${ackDue.format('ddd D MMM')}` }

  const classify = classified
    ? { key: 'met', label: incident.classification }
    : incident.status === 'Closed'
      ? { key: 'met', label: 'Closed' }
      : moment().isAfter(classifyDue)
        ? { key: 'overdue', label: `Classification overdue since ${classifyDue.format('D MMM')}` }
        : { key: 'due', label: `Classify by ${classifyDue.format('ddd D MMM')}` }

  return { acknowledge, classify }
}

export const isOpen = (incident) => incident?.status !== 'Closed'

export const openActions = (incident) =>
  (incident?.actions || []).filter((a) => a.status === 'Open' || a.status === 'In Progress')

export const overdueActions = (incident) =>
  openActions(incident).filter((a) => a.dueDate && moment(a.dueDate).isBefore(moment(), 'day'))

export const fullName = (user) =>
  user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : ''
